import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useTenant } from '../../../contexts/TenantContext';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * A stylized logout page for SAAS clients
 * Shows after successful logout with option to sign back in
 */
const SaasLogout: React.FC = () => {
  const { tenant, isAddEditHost } = useTenant();

  // Get the sign-in URL - for addedit domains, use the tenant path
  const getSignInUrl = () => {
    if (isAddEditHost() && tenant) {
      return `/${tenant}`;
    }
    return '/';
  };

  const handleSignIn = () => {
    window.location.href = getSignInUrl();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.secondary',
        padding: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: 6,
            textAlign: 'center',
            borderRadius: 2,
            background: 'background.paper',
            border: '2px solid',
            borderColor: 'grey.100',
          }}
        >
          {/* Success Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'success.main',
                boxShadow: 3,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 64,
                  color: 'background.paper',
                }}
              />
            </Box>
          </Box>

          {/* Logout Title */}
          <Typography
            variant="h3"
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              marginBottom: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Signed Out Successfully
          </Typography>

          {/* Logout Message */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: '400px',
              margin: '0 auto',
              marginBottom: 4,
              fontSize: '1rem',
            }}
          >
            You have been successfully signed out of your account. Thank you for
            using listing management.
          </Typography>

          {/* Sign In Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 3,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleSignIn}
              sx={{
                paddingX: 4,
                paddingY: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1,
                fontSize: '1rem',
                boxShadow: 2,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Sign In Again
            </Button>
          </Box>

          {/* Security Notice */}
          <Box
            sx={{
              marginTop: 4,
              padding: 2,
              backgroundColor: 'info.light',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LogoutIcon
              sx={{
                fontSize: 20,
                color: 'info.dark',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'info.dark',
                fontSize: '0.75rem',
                textAlign: 'left',
              }}
            >
              For your security, please close this browser tab if you're using a
              shared computer.
            </Typography>
          </Box>
        </Paper>

        {/* Debug Information (only shown in development) */}
        {process.env.NODE_ENV === 'development' && (
          <Box
            sx={{
              marginTop: 2,
              padding: 2,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
              }}
            >
              Debug: Logout page for tenant: {tenant || 'unknown'}
              <br />
              Sign-in URL: {getSignInUrl()}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SaasLogout;
